import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import usersApi from "api/landlord/renters";
import moment from "moment";
import addressApi from "api/landlord/address";
import { useAuthContext } from "providers/Auth";
import { Layout } from "providers/Layout";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  CreatableSelect,
  DateInput,
  Input,
  Select
} from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { useForm } from "shared/hooks";
import { Settings02, Upload01 } from "untitledui-js-base";
import { Row } from "./components/Row";
import { getErrorMessage } from "utils/error";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { getPropertyNameWithLongAddress } from "shared/addressUtils";
import { SubscribeModal } from "views/renters/components/Header/SubscribeModal";
import { AsyncSelect } from "shared/components/AsyncSelect/AsyncSelect";
import { loadBoomReportUnitOptions } from "api/landlord/asyncUnitProperties";
import { Unit } from "shared/types";
import api from "api/landlord/address";
import { Property } from "types/landlord/address";
import { enrollmentsNewValidations } from "./validations";
import { Redirect } from "shared/components/Redirect";
import { toast } from "react-toastify";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";

function capitalize(s: string) {
  if (s.length === 0) return s;
  if (s.length === 1) return s.toUpperCase();

  return s[0].toUpperCase() + s.slice(1).toLocaleLowerCase();
}

export const AddNewSubscriber = () => {
  const { Unit, Property } = useUnitsPropertiesDictionary();
  const [property, setProperty] = useState<Property | undefined>();
  const [showEnrollConfirmationModal, setShowEnrollConfirmationModal] =
    useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [debouncedSearch] = useDebouncedValue(searchQuery, 500);

  const navigate = useNavigate();

  const { type } = useAuthContext();

  const { data: propertiesData, isLoading } = useQuery({
    queryKey: ["PROPERTIES", { property: debouncedSearch }],
    queryFn: () =>
      addressApi
        .getProperties({
          per_page: 40,
          searchTerm: debouncedSearch
        })
        .then((data: { items: Property[] }) => data.items),
    placeholderData: keepPreviousData
  });

  const { register, values, handleChange, onSubmit, isSubmitting } = useForm(
    {
      firstName: "",
      lastName: "",
      dob: undefined,
      ssn: "",
      email: "",
      phone: "",
      unit: "",
      moveInDate: undefined,
      leaseEndDate: undefined,
      isMonthToMonth: false,
      selectedProperty: null,
      currentRentAmount: "",
      optOut: true,
      address: ""
    },
    enrollmentsNewValidations
  );

  const { mutate: enrollRenter, isPending } = useMutation({
    mutationKey: ["ENROLL_RENTER"],
    mutationFn: (enrollmentOption: { optIn: boolean; optOut: boolean }) => {
      return usersApi.addRenter({
        property_id: property?.id,
        email: values.email,
        first_name: values.firstName,
        last_name: values.lastName,
        phone_number: values.phone,
        dob:
          values.dob && moment(values.dob).isValid()
            ? moment(values.dob).format("YYYY-MM-DD")
            : undefined,
        ssn: values.ssn,
        move_in_date: values.moveInDate,
        lease_end_date: values.isMonthToMonth ? undefined : values.leaseEndDate,
        monthly_rent_amount: parseFloat(values.currentRentAmount),
        address1: property?.address.address1,
        city: property?.address.city,
        state_or_province: property?.address.state_or_province,
        postal_code: property?.address.postal_code,
        country: property?.address.country,
        lease_month_to_month: values.isMonthToMonth || undefined,
        no_communication: false,
        opt_in: enrollmentOption.optIn,
        opt_out: enrollmentOption.optOut,
        unit_id: values.unit
      });
    },
    onSuccess: () => {
      navigate("/rent-reporting");
      setShowEnrollConfirmationModal(false);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  const isSubmitBlocked = Boolean(
    isSubmitting || isLoading || isPending || showEnrollConfirmationModal
  );

  const { data: units } = useQuery({
    queryKey: ["GET_UNITS", String(property?.id)],
    queryFn: () =>
      api.getUnitsOfProperty({
        propertyId: property?.id || 0
      })
  });

  if (
    (type.isPMS && !type.isBoomOps && !type.canManageEntitiesDirectly) ||
    type.isSDK
  )
    return <Redirect href="/" />;

  return (
    <Layout
      headerClassName="pb-0 pt-3"
      header={
        <div className="border-b border-alpha-100">
          <div className="mb-3 ml-6">
            <BreadCrumbs
              crumbs={[
                {
                  title: "Rent reporting",
                  href: "/rent-reporting"
                },
                {
                  title: "Add new subscriber",
                  href: "/rent-reporting/add"
                }
              ]}
            />
          </div>
          {type.isManual ||
          (type.isBoomOps && type.isPMS) ||
          (type.isPMS && type.canManageEntitiesDirectly) ? (
            <div className="flex items-center border-t border-alpha-100 gap-4 bg-primary-20 px-6 py-4">
              <p className="text-sm text-primary-500">
                Need to add multiple subscribers? Try our Import Data feature
              </p>
              <Link to="/rent-reporting/upload">
                <Button
                  Icon={Upload01}
                  size={ButtonSize.XS}
                  variant={ButtonVariant.BASE}
                >
                  Import data
                </Button>
              </Link>
            </div>
          ) : null}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Link to="/rent-reporting">
            <Button size={ButtonSize.BASE} variant={ButtonVariant.BASE}>
              Cancel
            </Button>
          </Link>
          <Button
            size={ButtonSize.BASE}
            variant={ButtonVariant.PRIMARY}
            form="single-upload-form"
            type="submit"
            loading={isSubmitBlocked}
            disabled={isSubmitBlocked}
          >
            Add Subscriber
          </Button>
        </div>
      }
    >
      <form
        id="single-upload-form"
        className="animate-appear mx-auto max-w-[760px] pb-4"
        onSubmit={onSubmit({
          onSuccess: () => {
            if (!property) return toast.error(`${Property} not found`);

            setShowEnrollConfirmationModal(true);
          }
        })}
      >
        <div>
          <h2 className="text-base font-bold text-content-700">
            Personal Information
          </h2>
          <Row
            left={
              <Input
                id="firstName"
                {...register("firstName", (d) =>
                  capitalize(String(d.target.value || ""))
                )}
                type="text"
                hideOptional
                maxLength={20}
                label="Legal first name"
                placeholder="Legal first name"
              />
            }
            right={
              <Input
                id="lastName"
                {...register("lastName", (d) =>
                  capitalize(String(d.target.value || ""))
                )}
                type="text"
                hideOptional
                maxLength={20}
                label="Legal last name"
                placeholder="Legal last name"
              />
            }
          />

          <Row
            left={
              <Input
                id="email"
                {...register("email")}
                hideOptional
                label="Email address"
                placeholder="example@gmail.com"
                type="email"
              />
            }
            right={
              <Input
                id="phone"
                hideOptional
                {...register("phone")}
                label="Phone number"
                placeholder="(123) 000-000"
                type="tel"
              />
            }
          />
        </div>

        <div className="my-6 h-px bg-alpha-80" />

        <div className="mt-6">
          <h2 className="text-base font-bold text-content-700">
            Identity verification
          </h2>

          <p className="my-4 text-sm font-medium text-content-500">
            Please, provide at lease one of the following:
          </p>

          <Row
            right={
              <Input
                id="ssn"
                {...register("ssn", (d) => d.target.value, "___-__-____")}
                type="text"
                hideOptional
                placeholder="XXX-232-3232"
                label="SSN"
              />
            }
            left={
              <DateInput
                id="dob"
                {...register("dob")}
                label="Date of birth"
                hideOptional
                placeholder="MM/DD/YYYY"
                max={Number(moment().subtract(18, "years").toDate())}
                // min={Number(moment().subtract(110, "years").toDate())}
              />
            }
          />

          <div className="mt-2 flex items-center gap-4 rounded-lg border border-alpha-100 bg-success-50 p-4">
            <Settings02 size="20px" className="stroke-success-700" />
            <p className="text-sm font-medium text-success-800">
              Identity verification will be done automatically
            </p>
          </div>
        </div>

        <div className="my-6 h-px bg-alpha-80" />

        <div className="mt-6">
          <h2 className="text-base font-bold text-content-700">
            Lease details
          </h2>
          <Row
            left={
              type.isManual ? (
                <CreatableSelect
                  id="address"
                  {...register("address", (d) => {
                    setProperty(
                      propertiesData?.find(
                        (p) => String(p.id) === d.target.value
                      )
                    );
                    return d.target.value;
                  })}
                  searchable
                  label={Property}
                  className="flex-1"
                  loading={isLoading}
                  onInputChange={(query) => {
                    if (query) setSearchQuery(query);
                    else setSearchQuery("");
                  }}
                  handleCreate={() => {
                    navigate("/properties/add");
                  }}
                  formatCreateLabel={(inputValue) =>
                    `+ Create property "${inputValue}"`
                  }
                  options={[
                    ...(propertiesData?.map((property: Property) => {
                      return {
                        value: String(property.id),
                        label: getPropertyNameWithLongAddress(
                          property,
                          property.address
                        )
                      };
                    }) || [])
                  ]}
                />
              ) : (
                <Select
                  id="address"
                  {...register("address", (d) => {
                    setProperty(
                      propertiesData?.find(
                        (p) => String(p.id) === d.target.value
                      )
                    );
                    return d.target.value;
                  })}
                  searchable
                  label={Property}
                  className="flex-1"
                  loading={isLoading}
                  hideOptional
                  onInputChange={(query) => {
                    if (query) setSearchQuery(query);
                    else setSearchQuery("");
                  }}
                  options={[
                    ...(propertiesData?.map((property: Property) => {
                      return {
                        value: String(property.id),
                        label: getPropertyNameWithLongAddress(
                          property,
                          property.address
                        )
                      };
                    }) || [])
                  ]}
                />
              )
            }
            right={
              <AsyncSelect
                id="unit"
                label={Unit}
                searchable={true}
                hideOptional
                value={values.unit}
                // @ts-expect-error
                loadOptions={loadBoomReportUnitOptions}
                additional={{ page: 1, propertyId: property?.id }}
                onChange={handleChange("unit", (e) => e.target.value)}
                options={[
                  ...(units?.items?.map((unit: Unit) => {
                    return {
                      value: String(unit.id),
                      label: unit.title
                    };
                  }) || [])
                ]}
              />
            }
          />
          <Row
            left={
              <DateInput
                id="moveInDate"
                {...register("moveInDate")}
                placeholder="MM/DD/YYYY"
                label="Move-in date"
                hideOptional
              />
            }
            right={
              <div className="mb-4 flex-1 gap-6">
                <DateInput
                  id="leaseEndDate"
                  {...register("leaseEndDate")}
                  placeholder="MM/DD/YYYY"
                  label="Lease end date"
                  disabled={values.isMonthToMonth}
                  readOnly={values.isMonthToMonth}
                  hideOptional
                />
                <Checkbox
                  id="isMonthToMonth"
                  {...register("isMonthToMonth", (v) => v.target.checked)}
                  checked={values.isMonthToMonth}
                  label="It's month to month / no end date"
                />
              </div>
            }
          />

          <div className="money-input-2 w-1/2 pr-4">
            <Input
              id="currentRentAmount"
              {...register("currentRentAmount", (e) => {
                if (/^\d*(\.\d{0,2})?$/.test(e.target.value))
                  return e.target.value;

                return values.currentRentAmount;
              })}
              hideOptional
              type="money"
              label="Rent amount"
            />
          </div>
        </div>
      </form>
      <SubscribeModal
        isOpen={showEnrollConfirmationModal}
        setIsOpen={(opened) => {
          setShowEnrollConfirmationModal(opened);
        }}
        renterName={`${values.firstName} ${values.lastName}`}
        enrollRenters={enrollRenter}
      />
    </Layout>
  );
};
